import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { howtoApi } from '../../api/client';
import type { HowToCategory, HowToGuide } from '../../api/modules/howto';
import { useAuth } from '../../hooks/useAuth';

export function useHelpGuides() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, loading: authLoading } = useAuth();

  const [guides, setGuides] = useState<HowToGuide[]>([]);
  const [categories, setCategories] = useState<HowToCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const selectedCategoryId = searchParams.get('category') || 'all';
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  const setSelectedCategoryId = useCallback(
    (categoryId: string) => {
      if (categoryId === 'all') {
        setSearchParams({});
        return;
      }
      setSearchParams({ category: categoryId });
    },
    [setSearchParams]
  );

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [guidesRes, categoriesRes] = await Promise.all([
        howtoApi.listGuides(),
        howtoApi.listCategories(),
      ]);
      setGuides(guidesRes.guides);
      setCategories(categoriesRes.categories);
    } catch (err: any) {
      console.error('Failed to load help guides:', err);
      setError(err?.message || 'Failed to load help guides');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
    if (isAuthenticated) {
      load();
    }
  }, [authLoading, isAuthenticated, navigate, load]);

  const filteredGuides = useMemo(() => {
    const q = search.trim().toLowerCase();
    return guides.filter(g => {
      if (selectedCategoryId !== 'all' && g.categoryId !== selectedCategoryId) {
        return false;
      }
      if (q && !g.title.toLowerCase().includes(q)) {
        return false;
      }
      return true;
    });
  }, [guides, selectedCategoryId, search]);

  return {
    authLoading,
    isAuthenticated,
    isLoading,
    guides: filteredGuides,
    categories,
    selectedCategoryId,
    setSelectedCategoryId,
    search,
    setSearch,
    error,
  };
}
